import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClockRotateLeft, faTriangleExclamation, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { ActionVerdict, AssistantAction, AssistantActionStatus } from '../types';

interface ResolvedState {
  className: string;
  icon: IconDefinition;
  label: string;
}

const RESOLVED_STATES: Partial<Record<AssistantActionStatus, ResolvedState>> = {
  applied: { className: 'text-success', icon: faCheck, label: 'Accepted' },
  failed: { className: 'text-danger', icon: faTriangleExclamation, label: "Couldn't be applied" },
  rejected: { className: 'text-muted', icon: faXmark, label: 'Rejected' },
  stale: { className: 'text-muted', icon: faClockRotateLeft, label: 'This proposal is out of date' },
};

function getResolvedState({ automatic, status }: AssistantAction): ResolvedState | undefined {
  if (status === 'applied' && automatic) {
    return undefined;
  }

  return RESOLVED_STATES[status];
}

interface Props {
  actions: AssistantAction[];
  disabled: boolean;
  onResolve: (action: AssistantAction, verdict: ActionVerdict) => void;
}

export default function ActionConfirmations({ actions, disabled, onResolve }: Props) {
  return (
    <>
      {actions.map((action) => {
        const resolved = getResolvedState(action);

        return (
          <div className="mt-1" key={action.id}>
            {action.status === 'pending' && (
              <div className="d-flex gap-2">
                <Button
                  disabled={disabled}
                  icon={faCheck}
                  label="Accept"
                  size="sm"
                  onClick={() => onResolve(action, 'apply')}
                />
                <Button
                  disabled={disabled}
                  icon={faXmark}
                  label="Reject"
                  size="sm"
                  variant="outline"
                  onClick={() => onResolve(action, 'reject')}
                />
              </div>
            )}
            {action.status === 'applying' && (
              <div className="d-flex align-items-center gap-2 text-muted small">
                <LoadingSpinner inline size="sm" />
                <span>Applying…</span>
              </div>
            )}
            {resolved && (
              <div className={`d-flex align-items-center gap-2 small ${resolved.className}`}>
                <FontAwesomeIcon className="flex-shrink-0" icon={resolved.icon} />
                <span>{resolved.label}</span>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}